import { createFileRoute } from "@tanstack/react-router";
import { Briefcase } from "lucide-react";

export const Route = createFileRoute("/therapist-terms")({
  head: () => ({
    meta: [
      { title: "Therapist Directory Terms — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Therapist Directory Terms for Recovery Trauma™, including document verification, independent practitioner status, therapist responsibilities and client disclaimer.",
      },
      { property: "og:title", content: "Therapist Directory Terms — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "How the Recovery Trauma™ therapist directory works, including document verification before listing and the limits of that check.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/therapist-terms" },
    ],
    links: [
      { rel: "canonical", href: "https://recoverytrauma.com/therapist-terms" },
    ],
  }),
  component: TherapistTermsPage,
});

function TherapistTermsPage() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <header className="mb-8 text-center">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
          <Briefcase className="h-3.5 w-3.5" /> Therapist directory
        </div>
        <h1 className="font-serif text-4xl tracking-tight text-foreground md:text-5xl">
          Therapist Directory Terms
        </h1>
      </header>

      <div className="space-y-6 rounded-2xl border border-border bg-card p-6 text-sm leading-relaxed text-foreground/85">
        <p>
          Recovery Trauma™ provides a therapist discovery directory to help
          users find independent trauma-informed professionals.
        </p>

        <section className="space-y-3">
          <h2 className="font-serif text-xl text-foreground">
            Document Verification
          </h2>
          <p>
            Recovery Trauma Ltd operates the Therapist Directory as a directory
            and listing service only. Before a profile can be listed, we carry
            out document verification — not clinical assessment.
          </p>
          <p>As part of that check, we may review:</p>
          <ul className="list-disc space-y-2 ps-5">
            <li>Identity information</li>
            <li>Relevant qualification or certification documents</li>
            <li>Professional registration or membership where applicable</li>
            <li>Evidence of current professional indemnity insurance</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-serif text-xl text-foreground">
            Independent Practitioners
          </h2>
          <p>
            Therapists listed in the directory are independent practitioners.
            Recovery Trauma Ltd does not employ, supervise, clinically assess,
            recommend, endorse or guarantee therapists or their services.
          </p>
          <p>
            Recovery Trauma™ does not provide therapy through the directory and
            is not a party to the therapist–client relationship. Appointments,
            treatment, contracts and payments take place directly between the
            practitioner and the client.
          </p>
          <p>
            A therapist's presence in the directory, including any Documents
            Verified badge, must never be described or understood as clinical
            approval or endorsement by Recovery Trauma™.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-serif text-xl text-foreground">
            Client Disclaimer
          </h2>
          <p>
            Document verification confirms only that specified documents were
            provided and reviewed at a point in time. It does not guarantee a
            practitioner's competence, suitability, conduct, treatment or
            outcomes.
          </p>
          <p>
            Clients should independently check a practitioner's credentials and
            professional registration, and satisfy themselves that the
            practitioner is right for them, before engaging their services.
            Nothing in these terms affects your statutory rights.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-serif text-xl text-foreground">
            Therapist Responsibility
          </h2>
          <p>Therapists remain responsible for:</p>
          <ul className="list-disc space-y-2 ps-5">
            <li>The accuracy of their profile</li>
            <li>Their professional registration or licence</li>
            <li>Their insurance</li>
            <li>
              Their legal right to practise in their stated country, state,
              or jurisdiction
            </li>
            <li>Keeping their information up to date</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-serif text-xl text-foreground">
            Directory Disclaimer
          </h2>
          <p>
            Recovery Trauma™ does not charge users to access the directory
            and does not take referral fees or commission from therapy
            sessions booked through external therapist links.
          </p>
          <p>
            Recovery Trauma™ reserves the right to request updated
            documentation, suspend, hide, or remove a therapist profile if
            standards are not met or if information becomes inaccurate or
            outdated.
          </p>
        </section>
      </div>
    </div>
  );
}
